import React, { useState, useEffect, useMemo } from 'react';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { StyledForm, VaccinationOptions } from './style';
import { Button, Checkbox, Input, Radio, RadioChangeEvent } from 'antd';
import { useUpdateProfessionalClientVaccinations } from '../../../hooks/professionals/useClientGeneralInfo';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import useListOptionsForAllergiesAndOthers from '../../../hooks/common/useListOptions';
import CustomLoading from '../../Common/Loading';

const vaccinationOptions = [
  { id: '1', name: 'Covid-19' },
  { id: '2', name: 'DTP (Diphtheria, Tetanus and Polio)' },
  { id: '3', name: 'Hepatitis A' },
  { id: '4', name: 'Hepatitis B' },
  { id: '5', name: 'Influenza' },
  { id: '6', name: 'Japanese Encephalitis' },
  { id: '7', name: 'Meningitis' },
  { id: '8', name: 'Mumps, Measles, Rubella' },
  { id: '9', name: 'Rabies' },
  { id: '10', name: 'Tick encephalitis' },
  { id: '11', name: 'Tuberculosis' },
  { id: '12', name: 'Typhoid' },
  { id: '13', name: 'Yellow Fever' },
  { id: '14', name: 'Other' },
];

const VaccinationsForm = () => {
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const [form] = StyledForm.useForm();
  const { data: listOptions, loading: loadingListOptions } = useListOptionsForAllergiesAndOthers();
  const { update: updateProfessionalClientVaccination, loading: loadingUpdateProfessionalClientVaccination } = useUpdateProfessionalClientVaccinations();
  const [withVaccinations, setWithVaccinations] = useState<boolean>(Boolean(selectedClient?.vaccinations));
  const [selectedVaccinations, setSelectedVaccinations] = useState<Record<string, boolean>>({});

  const vaccinationsOptions = useMemo(() => {
    if (listOptions) {
      return listOptions?.vaccinations?.map((option: any) => ({
        value: option.id,
        label: option.name
      }));
    }
  }, [listOptions]);

  console.log('listOptions', listOptions)
  const vaccinations = useMemo(() => (selectedClient?.vaccinations || []), [selectedClient]);

  useEffect(() => {
    const initialFormValues = vaccinations.reduce((acc: any, vaccination) => {
      const vaccinationId = vaccination.vaccination?.id;
      acc[vaccinationId] = {
        id: vaccination.id,
        when: vaccination.when,
        physiologicalResponse: vaccination.physiologicalResponse,
        otherName: vaccination.otherName,
      };
      return acc;
    }, {});

    form.setFieldsValue(initialFormValues);
    setSelectedVaccinations(
      vaccinations.reduce((acc: any, vaccination) => {
        acc[vaccination.vaccination.id] = true;
        return acc;
      }, {})
    );
  }, [form, vaccinations]);

  const onFinish = async (values: any) => {
    const formattedValues = vaccinationOptions
      .filter((vaccination) => selectedVaccinations[vaccination.id])
      .map((vaccination) => ({
        id: values[vaccination.id]?.id,
        when: values[vaccination.id]?.when,
        otherName: values[vaccination.id]?.otherName || '',
        physiologicalResponse: values[vaccination.id]?.physiologicalResponse || '',
        optionVaccinationId: vaccination.id,
      }));

    console.log('Formatted values:', formattedValues);
  };

  const onChangeWithVaccination = ({ target: { value } }: RadioChangeEvent) => {
    setWithVaccinations(value);
  };

  const handleVaccinationSelection = (vaccinationId: string, checked: boolean) => {
    setSelectedVaccinations((prev) => ({
      ...prev,
      [vaccinationId]: checked,
    }));
  };

  if (loadingListOptions) {
    return <CustomLoading />
  }

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="vaccinations_form"
      onFinish={onFinish}
      initialValues={{}} // No initialValues needed as we're setting them in useEffect
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      <p>I have had vaccinations in the past</p>
      <Radio.Group
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        onChange={onChangeWithVaccination}
        value={withVaccinations}
        optionType="button"
        buttonStyle="solid"
      />
      {withVaccinations && (
        <VaccinationOptions>
          {vaccinationsOptions.map((vaccination: any) => (
            <div key={vaccination.value} style={{ marginBottom: '1rem' }}>
              <Checkbox
                checked={!!selectedVaccinations[vaccination.value]}
                onChange={(e) => handleVaccinationSelection(vaccination.value, e.target.checked)}
                style={{ marginBottom: '0.5rem' }}
              >
                {vaccination.label}
              </Checkbox>
              {selectedVaccinations[vaccination.value] && (
                <div>
                  <StyledForm.Item
                    name={[vaccination.value, 'id']}
                    initialValue={vaccination.value}
                    hidden
                  >
                    <Input />
                  </StyledForm.Item>
                  <StyledForm.Item
                    name={[vaccination.value, 'when']}
                    label="When?"
                    rules={[
                      {
                        required: true,
                        message: 'Please specify when you got this vaccination',
                      },
                    ]}
                  >
                    <Input />
                  </StyledForm.Item>
                  <StyledForm.Item
                    name={[vaccination.value, 'physiologicalResponse']}
                    label="Did you have any specific responses?"
                  >
                    <Input />
                  </StyledForm.Item>

                  {vaccination.label === 'Other' && (
                    <StyledForm.Item
                      name={[vaccination.value, 'otherName']}
                      label="Which vaccination?"
                      rules={[
                        {
                          required: true,
                          message: 'Please specify the vaccination name',
                        },
                      ]}
                    >
                      <Input />
                    </StyledForm.Item>
                  )}
                </div>
              )}
            </div>
          ))}
        </VaccinationOptions>
      )}

      <StyledForm.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button loading={loadingUpdateProfessionalClientVaccination} type="primary" htmlType="submit">
          Save
        </Button>
      </StyledForm.Item>
    </StyledForm>
  );
};

export default VaccinationsForm;
